/**
 * What the verifier can say, as data — the DOM half lives in `VerifierModal.ts`.
 *
 * **A mahjong deal is only verifiable once its hand is over.** The wall seed
 * reproduces the entire deal (`buildWall(seed)`), and the drand proof
 * reproduces the seed (`deriveMahjongSeed`), so `network/seatView.ts` withholds
 * both until `phase === 'hand-end'` — publishing either mid-hand would publish
 * every concealed hand. `verifiableDeal` below is therefore the *only* way the
 * UI is allowed to decide whether to offer verification: it returns `null` for
 * every state where the bundle is absent, which is every state before hand-end,
 * every plain (non-verifiable) room, and all of solo play.
 */

import type { DiceRollProof, ProofStatus } from '@bn-games/verifiable-rng';
import type { MahjongSnapshot } from '../network/protocol.js';

/** Everything needed to recompute a hand's wall from a public beacon. */
export interface VerifiableDeal {
  readonly proof: DiceRollProof;
  /** The derivation is room-scoped, so the code is part of the claim. */
  readonly roomCode: string;
  readonly seed: number;
}

/** `pending` is the verifier's own state while the BLS check is in flight. */
export type VerifyStatus = ProofStatus | 'pending';

export type VerifyTone = 'ok' | 'bad' | 'warn' | 'pending';

export type VerifyLabelKey =
  | 'verify.statusPending'
  | 'verify.statusVerified'
  | 'verify.statusSeedMismatch'
  | 'verify.statusSigInvalid'
  | 'verify.statusUnverified';

export interface VerifyView {
  readonly status: VerifyStatus;
  readonly glyph: string;
  readonly labelKey: VerifyLabelKey;
  readonly tone: VerifyTone;
}

export type VerifyDetailKey =
  | 'verify.source'
  | 'verify.chain'
  | 'verify.signedBy'
  | 'verify.round'
  | 'verify.randomness'
  | 'verify.room'
  | 'verify.seed'
  | 'verify.derivedSeed';

export interface VerifyDetail {
  readonly labelKey: VerifyDetailKey;
  readonly value: string;
  /** Full value behind an abbreviated one, for the row's `title`. */
  readonly full?: string;
}

/**
 * The deal the modal would verify, or `null` when there is nothing to verify.
 * The `phase` guard is deliberate belt-and-braces: a *filtered* snapshot already
 * carries `null` for both fields before hand-end, but an unfiltered one (the
 * shape the server builds) does not, and this must never be the place a live
 * seed escapes.
 */
export function verifiableDeal(
  snapshot: Pick<MahjongSnapshot, 'phase' | 'seed' | 'dealProof'>,
  roomCode: string | null,
): VerifiableDeal | null {
  if (snapshot.phase !== 'hand-end') return null;
  if (snapshot.dealProof === null || snapshot.seed === null) return null;
  if (roomCode === null || roomCode.length === 0) return null;
  return { proof: snapshot.dealProof, roomCode, seed: snapshot.seed };
}

export function verifyView(status: VerifyStatus): VerifyView {
  switch (status) {
    case 'pending':
      return { status, glyph: '…', labelKey: 'verify.statusPending', tone: 'pending' };
    case 'verified':
      return { status, glyph: '✓', labelKey: 'verify.statusVerified', tone: 'ok' };
    case 'value-mismatch':
      return { status, glyph: '✕', labelKey: 'verify.statusSeedMismatch', tone: 'bad' };
    case 'sig-invalid':
      return { status, glyph: '✕', labelKey: 'verify.statusSigInvalid', tone: 'bad' };
    case 'unverified':
      return { status, glyph: '⚠', labelKey: 'verify.statusUnverified', tone: 'warn' };
  }
}

/** Abbreviate a long hex value for display; the full string rides along as `full`. */
export function shortHex(hex: string, head = 8, tail = 6): string {
  if (hex.length <= head + tail + 1) return hex;
  return `${hex.slice(0, head)}…${hex.slice(-tail)}`;
}

/**
 * The provenance rows, in reading order: where the randomness came from, which
 * round it was, and what that round derives to. `derivedSeed` is only worth a
 * row when it disagrees with the recorded seed — when they agree, the seed row
 * already states it.
 */
export function verifyDetails(
  deal: VerifiableDeal,
  source: { readonly displayName: string; readonly signerLabel: string },
  derivedSeed: number | null,
): readonly VerifyDetail[] {
  const rows: VerifyDetail[] = [
    { labelKey: 'verify.source', value: source.displayName },
    { labelKey: 'verify.chain', value: shortHex(deal.proof.chainHash), full: deal.proof.chainHash },
    { labelKey: 'verify.signedBy', value: source.signerLabel },
    { labelKey: 'verify.round', value: String(deal.proof.round) },
    {
      labelKey: 'verify.randomness',
      value: shortHex(deal.proof.randomness),
      full: deal.proof.randomness,
    },
    { labelKey: 'verify.room', value: deal.roomCode },
    { labelKey: 'verify.seed', value: String(deal.seed) },
  ];
  if (derivedSeed !== null && derivedSeed !== deal.seed) {
    rows.push({ labelKey: 'verify.derivedSeed', value: String(derivedSeed) });
  }
  return rows;
}
